/**
 * Public `/account/google-revert#token=…` — the "This wasn't me" link in the
 * notice emailed to the sign-in address after a Google account was linked
 * while signed in (migration 090). The EmailRevert page's pattern: no sign-in
 * (the token is the authority), the token stays in the URL fragment, the page
 * only previews on load, and undoing is a button press, because mail scanners
 * open links. Undoing unlinks that Google account, signs the account out on
 * every device and revokes AI agent access created since. Works once, 14 days.
 */
import { useEffect, useState } from 'react';
import type { GoogleLinkReverted, GoogleLinkRevertPreview } from '../api/types';
import { useSession } from '../auth/session';
import { Link } from '../router';
import { ErrorNotice, Loading, formatDateTime } from '../ui/kit';
import { ApiError } from '../api/errors';
import { reportTokenFromHash } from './SignupReport';

export function GoogleLinkRevert() {
  const { api, status, signOut } = useSession();
  const [token] = useState(() => reportTokenFromHash(window.location.hash));
  const [preview, setPreview] = useState<GoogleLinkRevertPreview | null>(null);
  const [previewError, setPreviewError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [result, setResult] = useState<GoogleLinkReverted | null>(null);
  const [reverting, setReverting] = useState(false);
  const [revertError, setRevertError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api
      .previewGoogleLinkRevert(token)
      .then((p) => !cancelled && setPreview(p))
      .catch((err: unknown) => !cancelled && setPreviewError(err instanceof ApiError ? err : null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, token]);

  let body: React.ReactNode;
  if (!token) {
    body = (
      <div className="notice bad" role="alert">
        <span>This link is incomplete. Open the “This wasn’t me” link from the email again, or contact support@finderlyfix.com.</span>
      </div>
    );
  } else if (loading) {
    body = <Loading label="Checking the link" />;
  } else if (result) {
    body = (
      <div className="notice ok" role="status" data-testid="google-revert-done">
        <div className="stack-sm" style={{ gap: 4 }}>
          <strong>{result.google_unlinked ? 'Done. That Google account is unlinked.' : 'Done. That Google account was already unlinked.'}</strong>
          <span>We signed the account out on every device. Sign in with a code sent to {result.email}.</span>
          {result.agent_tokens_revoked > 0 ? (
            <span>
              We revoked {result.agent_tokens_revoked} AI agent {result.agent_tokens_revoked === 1 ? 'connection' : 'connections'} created
              after the link.
            </span>
          ) : null}
          <span>Tell support@finderlyfix.com what happened.</span>
          <span>
            <Link to="/sign-in">Sign in</Link>
          </span>
        </div>
      </div>
    );
  } else if (previewError || !preview) {
    body = <ErrorNotice error={previewError ?? new ApiError({ status: 404, code: 'GOOGLE_LINK_REVERT_LINK_INVALID' })} />;
  } else if (preview.used) {
    body = (
      <div className="notice info" role="status" data-testid="google-revert-used">
        <span>
          <strong>This link has already been used.</strong> It works once. If something still looks wrong, contact
          support@finderlyfix.com.
        </span>
      </div>
    );
  } else {
    body = (
      <div className="stack-sm">
        <p>A Google account was linked to your FinderlyFix partner sign-in:</p>
        <dl className="facts" data-testid="google-revert-facts">
          <dt>Your account</dt>
          <dd style={{ overflowWrap: 'anywhere' }}>{preview.account_email}</dd>
          <dt>Google account</dt>
          <dd>{preview.google_email ?? 'Not shown by Google'}</dd>
          <dt>When</dt>
          <dd>{formatDateTime(preview.linked_at)}</dd>
          <dt>Link works until</dt>
          <dd>{formatDateTime(preview.expires_at)}</dd>
        </dl>
        <p>If you didn’t do this, undo it. We unlink that Google account and sign the account out on every device.</p>
        <ErrorNotice error={revertError} />
        <div className="row">
          <button
            type="button"
            className="btn btn-danger"
            disabled={reverting}
            onClick={async () => {
              setReverting(true);
              setRevertError(null);
              try {
                const done = await api.revertGoogleLink(token);
                setResult(done);
                if (status !== 'signed_out' && status !== 'loading') await signOut().catch(() => undefined);
              } catch (err) {
                setRevertError(err instanceof ApiError ? err : new ApiError({ status: 500, code: 'INTERNAL_ERROR' }));
              } finally {
                setReverting(false);
              }
            }}
          >
            {reverting ? 'Undoing…' : 'This wasn’t me: unlink and sign out everywhere'}
          </button>
        </div>
        <p className="small muted">If you linked it, you don’t need this page.</p>
      </div>
    );
  }

  return (
    <main className="shared-dashboard legal-page" id="main">
      <header className="row-between legal-head">
        <Link to="/" className="eyebrow legal-home">
          FinderlyFix Partners
        </Link>
      </header>
      <article className="stack" aria-labelledby="google-revert-title">
        <h1 id="google-revert-title">This wasn’t me</h1>
        {body}
      </article>
    </main>
  );
}
